'use client'

import * as React from 'react'
import { X } from 'lucide-react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from './sheet'
import { cn } from '../../lib/utils'

interface BaseDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  /** Side to open from - automatically flips for RTL */
  side?: 'left' | 'right'
  /** Whether the current language is RTL */
  isRTL?: boolean
  /** Gradient classes for header, e.g. "from-emerald-500 to-teal-500" */
  headerGradient?: string
  /** Header content (icon, title, badges, etc.) */
  headerContent?: React.ReactNode
  /** Footer content (action buttons) */
  footerContent?: React.ReactNode
  /** Optional top bar content between header and main content */
  topBarContent?: React.ReactNode
  /** Sticky content that appears above scrollable area (e.g., tabs) - does not scroll with content */
  stickyContent?: React.ReactNode
  /** Custom width class, defaults to "sm:max-w-[500px]" */
  maxWidth?: string
  /** Fixed width in pixels - overrides maxWidth when set. On mobile (<640px), uses full width. */
  fixedWidth?: number
  /** Additional className for the outer container */
  className?: string
  /** Whether drawer is responsive (adapts to screen size) - defaults to true */
  responsive?: boolean
  /** Accessible title for screen readers (required for a11y) */
  title?: string
  /** Width on tablet (640px-1024px), defaults to fixedWidth or 90% */
  tabletWidth?: number | string
  /** Width on mobile (<640px), defaults to full width */
  mobileWidth?: number | string
  /**
   * Modal behavior - when true, blocks interaction outside the drawer.
   * Set to false when using Select, Combobox, or other portaled components inside the drawer.
   */
  modal?: boolean
  /** Whether clicking outside or pressing Escape should close the drawer. */
  dismissible?: boolean
  /** Optional className for the main content area. */
  contentClassName?: string
  /** Whether the main content area is vertically scrollable - defaults to true. */
  contentScrollable?: boolean
}

type DeviceType = 'mobile' | 'tablet' | 'desktop'

function BaseDrawer({
  open,
  onOpenChange,
  children,
  side = 'right',
  isRTL = false,
  headerGradient = 'from-primary to-primary/80',
  headerContent,
  footerContent,
  topBarContent,
  stickyContent,
  maxWidth = 'sm:max-w-[500px]',
  fixedWidth,
  className,
  responsive = true,
  title = 'Drawer',
  tabletWidth,
  mobileWidth,
  modal = false,
  dismissible = true,
  contentClassName,
  contentScrollable = true,
}: BaseDrawerProps) {
  // Check RTL from prop or document direction
  const effectiveRTL = isRTL || (typeof document !== 'undefined' && document.documentElement.dir === 'rtl')
  // Flip side for RTL
  const actualSide = effectiveRTL ? (side === 'right' ? 'left' : 'right') : side
  // Gradient direction for RTL
  const gradientDirection = effectiveRTL ? 'bg-gradient-to-l' : 'bg-gradient-to-r'

  // Responsive breakpoints: mobile < 640px, tablet 640-1024px, desktop >= 1024px
  const [deviceType, setDeviceType] = React.useState<DeviceType>('desktop')
  React.useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth
      if (width < 640) {
        setDeviceType('mobile')
      } else if (width < 1024) {
        setDeviceType('tablet')
      } else {
        setDeviceType('desktop')
      }
    }
    checkDevice()
    window.addEventListener('resize', checkDevice)
    return () => window.removeEventListener('resize', checkDevice)
  }, [])

  const isMobile = deviceType === 'mobile'
  const isTablet = deviceType === 'tablet'

  const handleFocusOutside = React.useCallback(
    (event: any) => {
      if (!modal) {
        event.preventDefault()
      }
    },
    [modal],
  )

  const handleInteractOutside = React.useCallback(
    (event: any) => {
      if (!dismissible) {
        event.preventDefault()
      }
    },
    [dismissible],
  )

  const handleEscapeKeyDown = React.useCallback(
    (event: any) => {
      if (!dismissible) {
        event.preventDefault()
      }
    },
    [dismissible],
  )

  // Helper to convert width value to CSS string
  const toWidthString = (value: number | string | undefined): string | undefined => {
    if (value === undefined) return undefined
    return typeof value === 'number' ? `${value}px` : value
  }

  // Calculate styles based on responsive behavior and device type
  const getStyles = (): React.CSSProperties | undefined => {
    if (!responsive) {
      // Non-responsive: use fixedWidth if set
      if (fixedWidth) {
        return { width: `${fixedWidth}px`, minWidth: `${fixedWidth}px`, maxWidth: `${fixedWidth}px` }
      }
      return undefined
    }

    // Mobile: use mobileWidth or default to full width
    if (isMobile) {
      const width = toWidthString(mobileWidth) || '100%'
      return { width, minWidth: width, maxWidth: width }
    }

    // Tablet: use tabletWidth or default to 90% (capped at fixedWidth if set)
    if (isTablet) {
      const width = toWidthString(tabletWidth) || (fixedWidth ? `${Math.min(fixedWidth, window.innerWidth * 0.9)}px` : '90%')
      return { width, minWidth: width, maxWidth: width }
    }

    // Desktop: use fixedWidth if set
    if (fixedWidth) {
      return { width: `${fixedWidth}px`, minWidth: `${fixedWidth}px`, maxWidth: `${fixedWidth}px` }
    }

    return undefined
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={modal}>
      <SheetContent
        side={actualSide}
        hideCloseButton
        onFocusOutside={handleFocusOutside}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        className={cn(
          'p-0 border-0 bg-transparent shadow-none rounded-3xl overflow-hidden',
          // Height adjustments by device
          isMobile ? 'h-[calc(100%-16px)]' : isTablet ? 'h-[calc(100%-24px)]' : 'h-[calc(100%-32px)]',
          // Margin adjustments by device
          isMobile ? 'm-2' : isTablet ? 'm-3' : 'm-4',
          // Width when fixedWidth is not set
          !fixedWidth && !isMobile && !isTablet && 'w-[calc(100%-32px)]',
          !fixedWidth && !isMobile && !isTablet && maxWidth,
          className
        )}
        style={getStyles()}
      >
        {/* Visually hidden title for screen reader accessibility */}
        <VisuallyHidden.Root>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription>{title}</SheetDescription>
        </VisuallyHidden.Root>
        <div className="bg-white dark:bg-slate-900 h-full rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-border">
          {/* Header */}
          {headerContent && (
            <div className={cn(
              'relative px-6 pt-10 pb-5',
              gradientDirection,
              headerGradient
            )}>
              <button
                onClick={() => onOpenChange(false)}
                className={cn(
                  "absolute top-10 w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors",
                  effectiveRTL ? "left-4" : "right-4"
                )}
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
              <div className={cn(effectiveRTL ? "pl-12" : "pr-12")}>
                {headerContent}
              </div>
            </div>
          )}

          {/* Optional Top Bar */}
          {topBarContent && (
            <div className="px-6 py-3 bg-slate-100 dark:bg-slate-800 border-b border-border flex items-center justify-between w-full">
              {topBarContent}
            </div>
          )}

          {/* Sticky Content (e.g., tabs) - does not scroll */}
          {stickyContent && (
            <div className="flex-shrink-0">
              {stickyContent}
            </div>
          )}

          {/* Content - scrollable area */}
          <div
            className={cn(
              'flex-1',
              contentScrollable ? 'overflow-y-auto overflow-x-hidden' : 'overflow-hidden',
              'p-6 space-y-5',
              contentClassName
            )}
          >
            {children}
          </div>

          {/* Footer */}
          {footerContent && (
            <div className="p-6 border-t border-border bg-slate-50 dark:bg-slate-800/50">
              {footerContent}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}

export { BaseDrawer, type BaseDrawerProps }
